import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ConfigService } from 'app/config.service';
import { ThemeService } from 'app/private/_core/_services/theme.service';
import { TooltipDirective } from 'app/shared/_directives/tooltip.directive';

@Component({
    selector: 'app-basic-button',
    templateUrl: './basic-button.component.html',
    styleUrls: ['./basic-button.component.scss'],
    imports: [
        CommonModule,
        RouterModule,
        AngularSvgIconModule,
    ]
})
export class BasicButtonComponent implements OnChanges, OnInit {

  @ViewChild(TooltipDirective) tooltipDirective = null;

  // General
  @Input() type: 'anchor' | 'button' = 'button';
  @Input() buttonStyle: 'primary' | 'secondary' | 'ghost' = 'primary';
  @Input() size: 'small' | 'medium' | 'large' = 'large';
  @Input() isCircle: boolean = false;
  @Input() stretchHorizontal: boolean = false;
  @Input() clickHandler: Function | null = null; // TODO We should remove completly this at one point once it is replaced by onClickEM in every use case of this component
  @Input() dataTest: string | null = null;
  @Input() isDropdown: boolean = false;
  @Input() showDropdown: boolean = false;
  @Input() isPartDropdown: boolean = false;
  // State
  @Input() isLoading: boolean = false;
  @Input() isDisabled: boolean = false;
  // Background
  @Input() customBackgroundColor: string | null = null;
  // Text
  @Input() text: string | null = null;
  @Input() customTextColor: string | null = null;
  @Input() isTextBold: boolean = false;
  @Input() count: number | null = null;
  @Input() textAlign: 'left' | 'right' | 'center' = 'center';
  // Icon
  @Input() icon: string | null = null;
  @Input() iconPosition: 'left' | 'right' = 'left';
  @Input() iconSize: number = 20;
  @Input() iconColor: string | null = null;
  // Link
  @Input() navigationRoute: string | null = null;
  @Input() isExternalURL: boolean = false;
  @Input() navigationTarget: '_blank' | '_self' = '_blank';

  @Output() onClickEM: EventEmitter<any> = new EventEmitter<any>(); // TODO use this emitter instead of clickHandler on all buttons

  displayBackgroundColor: string;
  displayBorderColor: string;
  displayIconColor: string;
  displayTextColor: string;
  displayOutlineColor: string;

  constructor(
    public configService: ConfigService,
    public themeService: ThemeService,
  ) {}

  ngOnInit(): void {
    this.setOutlineColor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'isLoading' in changes
      || 'customBackgroundColor' in changes
      || 'buttonStyle' in changes
    ) {
      this.setBackgroundColor();
      this.setBorderColor();
      this.setTextColor();
      this.setIconColor();
    }

    if ('customTextColor' in changes) {
      this.setTextColor();
    }

    if ('iconColor' in changes) {
      this.setIconColor();
    }
  }

  private setOutlineColor(): void {
    let outlineColor = this.buttonStyle === 'primary'
      ? this.displayBackgroundColor
      : this.displayBorderColor;

    if (!outlineColor) {
      outlineColor = 'var(--theme-primary-color)';
    }

    if (outlineColor.includes('var')) {
      const trimmedVariable = outlineColor.substring(4, outlineColor.length - 1);
      outlineColor = getComputedStyle(document.documentElement).getPropertyValue(trimmedVariable);
    }

    // 'bf' is suffix for setting hex colour to 75% opacity
    this.displayOutlineColor = outlineColor + "bf";
  }

  private setBackgroundColor(): void {
    if (
      this.customBackgroundColor
      && this.buttonStyle === 'primary'
    ) {
      this.displayBackgroundColor = this.customBackgroundColor;
      return;
    }

    if (this.buttonStyle === 'secondary' || this.buttonStyle === 'ghost' ) {
      this.displayBackgroundColor = 'transparent';
      return;
    }

    this.displayBackgroundColor = 'var(--theme-primary-color)';
  }

  private setBorderColor(): void {
    if (this.buttonStyle === 'primary' || this.buttonStyle === 'ghost') {
      this.displayBorderColor = 'transparent';
      return;
    }

    if (this.buttonStyle === 'secondary' && this.customBackgroundColor) {
      this.displayBorderColor = this.customBackgroundColor;
      return;
    }

    this.displayBorderColor = 'var(--theme-primary-color)';
  }

  private setTextColor(): void {
    if (this.customTextColor) {
      this.displayTextColor = this.customTextColor;
      return;
    }

    if (
      this.customBackgroundColor
      && this.buttonStyle === 'secondary'
    ) {
      this.displayTextColor = this.customBackgroundColor;
      return;
    }

    if (this.buttonStyle === 'primary') {
      this.displayTextColor = 'var(--theme-primary-text-icon-color)';
      return;
    }

    this.displayTextColor = 'var(--theme-primary-color)';
  }

  private setIconColor(): void {
    if (this.isLoading) {
      this.displayIconColor = 'transparent';
      return;
    }

    if (this.iconColor) {
      this.displayIconColor = this.iconColor;
      return;
    }

    if (
      this.customBackgroundColor
      && this.buttonStyle === 'secondary') {
      this.displayIconColor = this.customBackgroundColor;
      return;
    }

    if (this.buttonStyle === 'primary') {
      this.displayIconColor = 'var(--theme-primary-text-icon-color)';
      return;
    }

    this.displayIconColor = 'var(--theme-primary-color)';
  }

  public onClick(e: Event): void {
    e.stopPropagation();
    if (this.isDisabled) return;

    this.tooltipDirective?.removeTooltip();
    this.clickHandler ? this.clickHandler() : this.onClickEM.emit(); // TODO update to only use onClickEM once we remove clickHandler
  }

}
